/**
 * Enterprise-grade Error Types and Utilities
 * Comprehensive error handling for ETH Pass Portal
 */

/**
 * Application Error Classes
 */
export class AppError extends Error {
  public readonly isOperational: boolean
  public readonly statusCode: number
  public readonly code: string
  public readonly timestamp: string
  public readonly context?: Record<string, any>

  constructor(
    message: string,
    statusCode: number = 500,
    code: string = 'INTERNAL_ERROR',
    isOperational: boolean = true,
    context?: Record<string, any>
  ) {
    super(message)
    this.name = this.constructor.name
    this.statusCode = statusCode
    this.code = code
    this.isOperational = isOperational
    this.timestamp = new Date().toISOString()
    if (context !== undefined) {
      this.context = context
    }
    Error.captureStackTrace(this, this.constructor)
  }
}

export class ValidationError extends AppError {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 400, 'VALIDATION_ERROR', true, context)
  }
}

export class AuthenticationError extends AppError {
  constructor(message: string = 'Authentication required', context?: Record<string, any>) {
    super(message, 401, 'AUTHENTICATION_ERROR', true, context)
  }
}

export class AuthorizationError extends AppError {
  constructor(message: string = 'Insufficient permissions', context?: Record<string, any>) {
    super(message, 403, 'AUTHORIZATION_ERROR', true, context)
  }
}

export class NotFoundError extends AppError {
  constructor(resource: string, context?: Record<string, any>) {
    super(`${resource} not found`, 404, 'NOT_FOUND', true, context)
  }
}

export class ConflictError extends AppError {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 409, 'CONFLICT', true, context)
  }
}

export class RateLimitError extends AppError {
  constructor(retryAfter?: number) {
    super('Too many requests', 429, 'RATE_LIMIT_EXCEEDED', true, { retryAfter })
  }
}

export class NetworkError extends AppError {
  constructor(message: string = 'Network error occurred', context?: Record<string, any>) {
    super(message, 0, 'NETWORK_ERROR', true, context)
  }
}

export class TimeoutError extends AppError {
  constructor(message: string = 'Request timeout', context?: Record<string, any>) {
    super(message, 408, 'TIMEOUT', true, context)
  }
}

export class BusinessLogicError extends AppError {
  constructor(message: string, code: string, context?: Record<string, any>) {
    super(message, 422, code, true, context)
  }
}

/**
 * Error severity levels for monitoring
 */
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
  INFO = 'INFO',
}

/**
 * Error metadata for enhanced tracking
 */
export interface ErrorMetadata {
  userId?: string
  tenantId?: string
  sessionId?: string
  requestId?: string
  path?: string
  method?: string
  userAgent?: string
  ip?: string
  correlationId?: string
  severity?: ErrorSeverity
  tags?: string[]
  fingerprint?: string[]
}

/**
 * Structured error response
 */
export interface ErrorResponse {
  success: false
  error: {
    message: string
    code: string
    statusCode: number
    timestamp: string
    requestId?: string
    details?: any
    path?: string
    suggestion?: string
  }
  metadata?: Record<string, any>
}

/**
 * Error recovery strategies
 */
export interface ErrorRecoveryStrategy {
  retry?: {
    attempts: number
    delay: number
    backoff?: 'linear' | 'exponential'
  }
  fallback?: () => any
  redirect?: string
  toast?: {
    message: string
    type: 'error' | 'warning' | 'info'
  }
}

/**
 * Type guards for error checking
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError
}

export function isNetworkError(error: unknown): boolean {
  if (error instanceof NetworkError) return true
  if (error instanceof Error) {
    return (
      error.message.toLowerCase().includes('network') ||
      error.message.toLowerCase().includes('fetch')
    )
  }
  return false
}

export function isAuthError(error: unknown): boolean {
  return error instanceof AuthenticationError || error instanceof AuthorizationError
}

export function isRetryableError(error: unknown): boolean {
  if (error instanceof AppError) {
    return [408, 429, 502, 503, 504].includes(error.statusCode)
  }
  return false
}

/**
 * Error code constants
 */
export const ERROR_CODES = {
  // Authentication & Authorization
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_TOKEN_EXPIRED: 'AUTH_TOKEN_EXPIRED',
  AUTH_TOKEN_INVALID: 'AUTH_TOKEN_INVALID',
  AUTH_SESSION_EXPIRED: 'AUTH_SESSION_EXPIRED',
  AUTH_2FA_REQUIRED: 'AUTH_2FA_REQUIRED',
  AUTH_2FA_INVALID: 'AUTH_2FA_INVALID',
  AUTH_ACCOUNT_LOCKED: 'AUTH_ACCOUNT_LOCKED',
  AUTH_PERMISSION_DENIED: 'AUTH_PERMISSION_DENIED',

  // Validation
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  INVALID_INPUT: 'INVALID_INPUT',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',
  INVALID_FORMAT: 'INVALID_FORMAT',

  // Business Logic
  ENROLLMENT_DUPLICATE: 'ENROLLMENT_DUPLICATE',
  ENROLLMENT_INCOMPLETE: 'ENROLLMENT_INCOMPLETE',
  BIOMETRIC_MISMATCH: 'BIOMETRIC_MISMATCH',
  DOCUMENT_VERIFICATION_FAILED: 'DOCUMENT_VERIFICATION_FAILED',
  TENANT_LIMIT_EXCEEDED: 'TENANT_LIMIT_EXCEEDED',

  // System
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  DATABASE_ERROR: 'DATABASE_ERROR',
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',

  // Network
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  CONNECTION_REFUSED: 'CONNECTION_REFUSED',
} as const

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES]
